import React, { useEffect, useState } from "react";
import { APIService } from "../../../shared/shared-with-mobile/api-client/api.service";
import { FunnelResult } from "../../../shared/shared-with-mobile/api-client/services/admin-analytics.service";

const WINDOW_OPTIONS: { label: string; days: number }[] = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "All time", days: 0 },
];

const STAGE_COLORS = ["#1565c0", "#1976d2", "#2196f3", "#42a5f5", "#64b5f6"];

const ActivationFunnelChart: React.FC = () => {
  const [windowDays, setWindowDays] = useState<number>(30);
  const [result, setResult] = useState<FunnelResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    APIService.ADMIN_ANALYTICS.GET_FUNNEL(windowDays)
      .then((data) => {
        if (!alive) return;
        setResult(data);
      })
      .catch((e) => {
        if (!alive) return;
        console.error("funnel load failed", e);
        setError("Failed to load activation funnel.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [windowDays]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        {WINDOW_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            onClick={() => setWindowDays(opt.days)}
            style={{
              border:
                windowDays === opt.days
                  ? "1px solid #1565c0"
                  : "1px solid #424258",
              background:
                windowDays === opt.days
                  ? "rgba(33, 150, 243, 0.18)"
                  : "transparent",
              color: windowDays === opt.days ? "#1565c0" : "#ababb7",
              padding: "4px 12px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: "#ababb7" }}>Loading funnel...</div>}
      {error && <div style={{ color: "#F44336" }}>{error}</div>}
      {result && !loading && (
        <>
          <div
            style={{
              fontSize: 12,
              color: "#ababb7",
              marginBottom: 12,
            }}
          >
            Cohort: {result.cohortSize.toLocaleString()} signups
            {result.cohortStartDate
              ? ` since ${result.cohortStartDate}`
              : " (all time)"}
            . Each row shows the cohort members who reached that stage. The
            percent-of-prev column = conversion rate from the previous stage.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {result.stages.map((stage, i) => {
              // Bar width relative to max stage count (cohortSize)
              const maxCount = result.stages[0]?.count || 1;
              const barWidth =
                maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
              return (
                <div
                  key={stage.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "180px 1fr 200px",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                    {i + 1}. {stage.label}
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      borderRadius: 4,
                      height: 28,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background: STAGE_COLORS[i] || "#1565c0",
                        width: `${barWidth}%`,
                        height: "100%",
                        transition: "width 0.4s ease",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: 8,
                        top: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#fff",
                        textShadow: "0 0 4px rgba(0,0,0,0.6)",
                      }}
                    >
                      {stage.count.toLocaleString()}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#ababb7",
                      textAlign: "right",
                    }}
                  >
                    {i === 0 ? (
                      <span>baseline</span>
                    ) : (
                      <>
                        <span
                          style={{
                            color:
                              stage.percentOfPrevStage >= 50
                                ? "#4CAF50"
                                : stage.percentOfPrevStage >= 25
                                ? "#FFC107"
                                : "#F44336",
                          }}
                        >
                          {stage.percentOfPrevStage.toFixed(1)}% of prev
                        </span>{" "}
                        ·{" "}
                        <span>{stage.percentOfTotal.toFixed(1)}% of total</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ActivationFunnelChart;
